/**
 * Ferien & Feiertage über die OpenHolidays API (openholidaysapi.org): ein
 * offen betriebenes, kostenloses EU-Projekt ohne API-Key und mit offenem
 * CORS – verlässlicher als kleine Einzelprojekt-APIs. Vorerst nur
 * Schleswig-Holstein (DE-SH); weitere Bundesländer liessen sich über
 * `SUBDIVISION` leicht ergänzen.
 */

const BASE_URL = "https://openholidaysapi.org";
const COUNTRY = "DE";
const SUBDIVISION = "DE-SH";
const LANGUAGE = "DE";

export type HolidayKind = "school" | "public";

export interface Holiday {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
  name: string;
  kind: HolidayKind;
}

interface ApiEntry {
  startDate: string;
  endDate: string;
  name: { language: string; text: string }[];
  /** Gesetzt bei Sonderfällen, z. B. abweichende Termine für die Inseln/Halligen. */
  comment?: { language: string; text: string }[];
  tags?: string[];
}

async function fetchKind(path: "PublicHolidays" | "SchoolHolidays", year: number): Promise<Holiday[]> {
  const url =
    `${BASE_URL}/${path}?countryIsoCode=${COUNTRY}&languageIsoCode=${LANGUAGE}` +
    `&validFrom=${year}-01-01&validTo=${year}-12-31&subdivisionCode=${SUBDIVISION}`;
  const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
  if (!res.ok) throw new Error(`Ferien-API (${path}): ${res.status}`);
  const data = (await res.json()) as ApiEntry[];
  return data
    // Sonderregelungen (z. B. "Inseln Sylt, Föhr, Amrum, Helgoland und die
    // Halligen" bei den Herbstferien) tragen `comment`/`tags: ["Exception"]`
    // und weichen von den regulären SH-Terminen ab – nur die reguläre
    // Variante übernehmen, sonst zeigt der Stundenplan das falsche Datum.
    .filter((d) => !d.comment?.length && !d.tags?.includes("Exception"))
    .map((d) => ({
      start: d.startDate.slice(0, 10),
      end: d.endDate.slice(0, 10),
      name: d.name.find((n) => n.language === LANGUAGE)?.text ?? d.name[0]?.text ?? "",
      kind: path === "PublicHolidays" ? "public" : ("school" as HolidayKind),
    }));
}

// Jahresweise gecached (Modul-Singleton) – bleibt für die Dauer der Session
// gültig, Ferientermine ändern sich nicht während der Nutzung.
const yearCache = new Map<number, Promise<Holiday[]>>();
const storeKey = (year: number) => `holidays:${SUBDIVISION}:${year}`;
const STORE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

function readStored(year: number): { at: number; data: Holiday[] } | null {
  try {
    return JSON.parse(localStorage.getItem(storeKey(year)) ?? "null");
  } catch {
    return null;
  }
}

function fetchYear(year: number): Promise<Holiday[]> {
  return Promise.all([fetchKind("PublicHolidays", year), fetchKind("SchoolHolidays", year)]).then(([pub, school]) => {
    const data = [...pub, ...school];
    try {
      localStorage.setItem(storeKey(year), JSON.stringify({ at: Date.now(), data }));
    } catch {
      /* Speicher voll – dann eben nur für diese Sitzung */
    }
    return data;
  });
}

function yearData(year: number): Promise<Holiday[]> {
  let p = yearCache.get(year);
  if (!p) {
    const stored = readStored(year);
    if (stored) {
      if (Date.now() - stored.at > STORE_MAX_AGE) void fetchYear(year).catch(() => undefined);
      p = Promise.resolve(stored.data);
    } else {
      // API nicht erreichbar o. Ä.: still leer liefern, Stundenplan bleibt ohne Markierung nutzbar.
      p = fetchYear(year).catch(() => [] as Holiday[]);
    }
    yearCache.set(year, p);
  }
  return p;
}

export const holidaysService = {
  /** Ferien + Feiertage, die in den angegebenen Zeitraum fallen (jahresweise gecached). */
  async forRange(from: Date, to: Date): Promise<Holiday[]> {
    const years: number[] = [];
    for (let y = from.getFullYear(); y <= to.getFullYear(); y++) years.push(y);
    const lists = await Promise.all(years.map(yearData));
    return lists.flat();
  },
};

/** Ferien-/Feiertag an einem bestimmten Tag, falls vorhanden. `dateIso` = YYYY-MM-DD. */
export function findHoliday(holidays: Holiday[], dateIso: string): Holiday | undefined {
  return holidays.find((h) => dateIso >= h.start && dateIso <= h.end);
}

/**
 * Ende der nächsten Sommerferien ab `from` – Grenze für „Termine bis zu den
 * Sommerferien vorschlagen". Steckt `from` bereits mitten in den
 * Sommerferien (Schuljahr ist praktisch vorbei), zählt die übernächste
 * Ausgabe, da sonst kaum noch Schultage bis zur Grenze übrig blieben.
 */
export function nextSummerBreakEnd(holidays: Holiday[], from: Date): Date | undefined {
  const fromIso = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, "0")}-${String(from.getDate()).padStart(2, "0")}`;
  const summer = holidays
    .filter((h) => h.kind === "school" && h.name.includes("Sommerferien"))
    .sort((a, b) => a.start.localeCompare(b.start));
  const upcoming = summer.find((h) => h.start > fromIso) ?? summer.find((h) => h.end >= fromIso);
  return upcoming ? new Date(`${upcoming.end}T00:00:00`) : undefined;
}
